import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React from 'react';

interface HtmlViewerProps {
  content: string;
  className?: string;
}

/**
 * HtmlViewer component for displaying HTML content in read-only mode using TipTap
 *
 * This component renders HTML content as a read-only rich text display,
 * preserving formatting, links, images, and other rich content.
 */
export const HtmlViewer: React.FC<HtmlViewerProps> = ({
  content,
  className = '',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable extensions we want to configure separately
        link: false,
        underline: false, // Disable underline from StarterKit to avoid duplicates
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: true, // Allow clicking links in read-only mode
        HTMLAttributes: {
          class: 'text-blue-600 underline cursor-pointer hover:text-blue-800',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-md',
        },
      }),
      Table.configure({
        resizable: false, // Disable resizing in read-only mode
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content,
    editable: false, // Read-only mode
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none dark:prose-invert focus:outline-none ${className}`,
      },
    },
  });

  if (!editor) {
    return null;
  }

  return <EditorContent editor={editor} />;
};

HtmlViewer.displayName = 'HtmlViewer';
